import React, {
  RefObject,
  useRef,
  useState,
  useEffect,
  useContext,
} from 'react';
import * as Yup from 'yup';
import { Avatar, Button } from 'antd';
import { Formik } from 'formik';
import { Form, Input, SubmitButton } from 'formik-antd';
import cx from 'classnames';
import { throttle } from 'lodash';

import FormItem from 'components/formik/FormItem';
import { getAvatarContent } from 'utils/personUtils';
import { MainLayoutContext } from 'components/AppLayout/MainLayout';
import { MemberCommentViewModel } from 'api/adminApiClient';

import styles from './CommentInput.module.less';

const validationSchema = Yup.object({
  comment: Yup.string()
    .trim()
    .max(5000, 'Comment should be 5000 characters maximum'),
});

export interface SubmitComment {
  comment: string;
  commentId?: string;
}

type Props = {
  onSubmit: (data: SubmitComment) => Promise<boolean | undefined>;
  scrollRef: RefObject<HTMLElement>;
  parentRef: RefObject<HTMLElement>;
  className?: string;
  onClick?: () => void;
  comment?: MemberCommentViewModel;
  onClose?: () => void;
  isEditMode?: boolean;
};

interface CommentFormValues {
  comment: string;
}

export default function CommentInput(props: Props) {
  const {
    onSubmit,
    onClick,
    parentRef,
    scrollRef,
    comment,
    onClose,
    isEditMode,
  } = props;
  const layoutContextValue = useContext(MainLayoutContext);
  const [isAddMode, setIsAddMode] = useState(isEditMode);
  const [isCommentInputFixed, setIsCommentInputFixed] = useState(false);
  const shouldCommentInputBeFixed = !isAddMode && isCommentInputFixed;
  const { id: commentId, text, lastName, firstName } = comment || {};

  const formRef: any = useRef(null);
  const initialValues: CommentFormValues = {
    comment: text || '',
  };

  useEffect(() => {
    const handleScroll = throttle((event: any) => {
      const isCommentInputHidden =
        (event.target.scrollTop || 0) > (parentRef?.current?.offsetTop || 0);

      if (isCommentInputHidden && !isCommentInputFixed) {
        setIsCommentInputFixed(true);
      } else if (!isCommentInputHidden && isCommentInputFixed) {
        setIsCommentInputFixed(false);
      }
    }, 100);

    const scrollElement = scrollRef?.current;

    scrollElement?.addEventListener('scroll', handleScroll);

    return () => {
      scrollElement?.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line
  }, [isCommentInputFixed]);

  const handleFocus = () => {
    setIsAddMode(true);

    onClick?.();
  };

  const handleCancel = () => {
    setIsAddMode(false);
    formRef?.current?.resetForm();
    onClose?.();
  };

  const handleSubmit = async (values: CommentFormValues) => {
    if (!values.comment.trim()) return;

    const notHaveChanges =
      initialValues.comment.trim() === values.comment.trim();

    if (notHaveChanges) {
      onClose?.();
      setIsAddMode(false);
      return;
    }

    const isSuccess = await onSubmit({
      comment: values.comment,
      commentId: commentId,
    });

    if (!isSuccess) return;
    onClose?.();
    setIsAddMode(false);
    formRef?.current?.resetForm();
  };

  return (
    <>
      {/* this div is needed to be like placeholder */}
      <div
        className={cx({
          [styles.commentPlaceholder]: shouldCommentInputBeFixed,
        })}
      />
      <Formik
        innerRef={formRef}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        initialValues={initialValues}
      >
        <Form
          className={cx('comment-input', {
            [styles.commentInput]: shouldCommentInputBeFixed,
            [styles.isSidebarCollapsed]:
              !layoutContextValue?.isSidebarCollapsed,
          })}
        >
          <div className={styles.wrapper}>
            {lastName && firstName && (
              <Avatar className={styles.avatar} size={24}>
                {getAvatarContent(firstName, lastName)}
              </Avatar>
            )}

            <div className={styles.holder}>
              <FormItem name="comment" className="full-width">
                <Input.TextArea
                  autoSize={{
                    minRows: 2,
                  }}
                  name="comment"
                  placeholder={
                    isAddMode && text ? 'Edit a comment' : 'Add a comment'
                  }
                  onFocus={handleFocus}
                />
              </FormItem>

              {isAddMode && (
                <div className={styles.actionBlock}>
                  <Button onClick={handleCancel}>Cancel</Button>

                  <SubmitButton disabled={false} loading={false}>
                    {isAddMode && text ? 'Save' : 'Add'}
                  </SubmitButton>
                </div>
              )}
            </div>
          </div>
        </Form>
      </Formik>
    </>
  );
}
